/* eslint-disable react-hooks/exhaustive-deps */
import Loader from 'components/loader/Loader';
import { LoadingModal } from 'components/modals/LoadingModal/LoadingModal';
import { SavingModal } from 'components/modals/SavingModal/SavingModal';
import { ServiceTypes } from 'constants/ServiceTypes';
import dayjs from 'dayjs';
import { TabsNames } from 'enums/TabsNames';
import { History } from 'history';
import { ISession } from 'interfaces/session/ISession';
import { ISessionUpdateRequest } from 'interfaces/session/ISessionUpdateRequest';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { SessionVerificationApiService } from 'services/data-services/SessionVerificationService';
import { useInjection } from 'services/ioc/ioc.react';
import { LogService } from 'services/LogService';
import { PlaysGridRowManager } from 'services/PlaysGridRowManager';
import { AutosaveStore } from 'stores/AutosaveStore';
import { ColumnsStore } from 'stores/ColumnsStore';
import { FilterStore } from 'stores/FilterStore';
import { SessionPlayStore } from 'stores/SessionPlayStore';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { UiStore } from 'stores/UiStore';
import { notify } from '../../services/NotificationService';
import { BallTrackingColumns, PlayerTrackingColumns } from './Components/EditPlaysGrid/EditPlaysColumnConfigurations';
import { EditPlaysGrid } from './Components/EditPlaysGrid/EditPlaysGrid';
import { TopBar } from './Components/TopBar/TopBar';
import { LoaderWrapper, PlayGrid } from './styles/EditStyles';

interface IProps {
  history: History;
  match: any;
  location: any;
}
const ROW_HEIGHT = 60;
export const Edit: React.FC<IProps> = observer(({ match, history }) => {
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const logService = useInjection<LogService>(ServiceTypes.LogService);
  const sessionStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const [savingModalVisible, setSavingModalVisible] = useState<boolean>(false);
  const sessionService = useInjection<SessionVerificationApiService>(ServiceTypes.SessionVerificationApiService);
  const sessionPlayStore = useInjection<SessionPlayStore>(ServiceTypes.SessionPlayStore);
  const [session, setSession] = useState<ISession>();
  const [shouldReturn, setShouldReturn] = useState<boolean>(false);
  const { sessionId } = match.params;
  const [loadingCsv, setLoadingCsv] = useState<boolean>(false);
  const autosaveStore = useInjection<AutosaveStore>(ServiceTypes.AutosaveStore);
  const rowManagerRef = useRef<PlaysGridRowManager>(new PlaysGridRowManager(autosaveStore, sessionId, sessionStore, ROW_HEIGHT));
  const [isDisplayingWarmups, setIsDisplayingWarmups] = useState<boolean>(rowManagerRef.current.isDisplayingWarmups);
  const filterStore = useInjection<FilterStore>(ServiceTypes.FilterStore);
  const columnsStore = useInjection<ColumnsStore>(ServiceTypes.ColumnsStore);

  useEffect(() => {
    const currentSession = sessionStore.sessions.find((session) => session.id === sessionId);
    if (currentSession && currentSession.isLocked) {
      history.push(`/search/teamId/undefined/date/${dayjs(currentSession.utcStartTime).format('YYYY-MM-DD')}?locked=true`);
    }
  }, [sessionStore.isLockMessageReceived]);

  useEffect(() => {
    logService.setSessionId(sessionId);
    getSession();
    uiStore.setIsNavbarVisible(false);
    autosaveStore.start();

    return () => {
      autosaveStore.stop();
      uiStore.setIsNavbarVisible(true);
      uiStore.setActiveTab(TabsNames.BallTracking);
      sessionPlayStore.resetPlays();
      logService.clearSession();
      filterStore.cleanupStore();
      columnsStore.cleanupStore();
    };
  }, []);

  const getSession = async () => {
    const sessionTask = sessionService.getSessionById(sessionId);
    const playsTask = sessionPlayStore.loadPlays(sessionId);
    const [session] = await Promise.all([sessionTask, playsTask]);
    if (sessionStore.verifySessionProperties(session)) {
      sessionPlayStore.loadRosters(session.homeTeam?.id, session.awayTeam?.id);
    } else if (session.isBattingPractice) {
      notify('Limited mode', "Batting Practice sessions don't support editing play data. Plays won't be loaded", 'info');
    } else {
      notify(
        'Limited mode',
        "Plays weren't loaded because important session metadata is missing. Edit session settings and try again",
        'info'
      );
    }

    if (session.isLocked) {
      history.push(`/search/teamId/undefined/date/${dayjs(session.utcStartTime).format('YYYY-MM-DD')}?locked=true`);
    }

    setSession(session);
  };

  const handleOnSubmitCallback = async (sessionUpdate: ISessionUpdateRequest) => {
    setSavingModalVisible(true);

    const update = await sessionPlayStore.updateSession(
      sessionUpdate,
      rowManagerRef.current.defaultRows.map((p) => p.toApiPlay())
    );
    if (update) {
      setShouldReturn(true);
    } else {
      setSavingModalVisible(false);
    }
  };

  const loadCsv = async (file: any) => {
    if (session) {
      setLoadingCsv(true);
      sessionPlayStore.processCsvRows(file, session);
      setTimeout(() => {
        setLoadingCsv(false);
      }, 1000);
    }
  };

  const onToggleWarmupsChanged = (newVal: boolean) => {
    setIsDisplayingWarmups(newVal);
    rowManagerRef.current.setIsDisplayingWarmups(newVal);
  };

  const onSessionUpdatedHandler = (oldSession: ISessionUpdateRequest, newSession: ISessionUpdateRequest) => {
    if (oldSession.awayTeam?.id !== newSession.awayTeam.id || oldSession.homeTeam?.id !== newSession.homeTeam.id) {
      sessionPlayStore.loadRosters(newSession.homeTeam.id, newSession.awayTeam.id);
      setSession({ ...session!, ...newSession });
      rowManagerRef.current.handleTeamsChanged(
        newSession.homeTeam,
        newSession.awayTeam,
        oldSession.homeTeam?.id !== newSession.homeTeam.id,
        oldSession.awayTeam?.id !== newSession.awayTeam.id
      );
    }
  };

  return (
    <div>
      <TopBar
        session={session}
        onSubmitCallback={handleOnSubmitCallback}
        onCsvSelected={loadCsv}
        shouldDisplayWarmups={isDisplayingWarmups}
        shouldDisplayWarmupsCallback={onToggleWarmupsChanged}
        onSessionUpdated={onSessionUpdatedHandler}
        rowManager={rowManagerRef.current}
      />
      <PlayGrid>
        {session && sessionStore.verifySessionProperties(session) ? (
          <EditPlaysGrid
            columns={uiStore.activeTab === TabsNames.BallTracking ? BallTrackingColumns : PlayerTrackingColumns}
            session={session}
            rowManager={rowManagerRef.current}
            history={history}
          />
        ) : (
          <LoaderWrapper>
            <Loader size='large' />
          </LoaderWrapper>
        )}
      </PlayGrid>
      <SavingModal history={history} shouldReturn={shouldReturn} visible={savingModalVisible} />
      <LoadingModal title='Loading' text='Loading csv, please wait..' visible={loadingCsv} />
    </div>
  );
});
